import { LightningElement, api, wire } from "lwc";
import classNames from "classnames";
import { logger } from "@devforce/trailhead-client-logger";

import { LabelsAdapter, LabelsAdapterValue } from "common/labelsAdapter";
import { assignedSlotNames } from "common/slot";
import { BooleanAttr, attrToBool, getAssetsUrl } from "common/helpers";

import * as locales from "./locales";
import { ToastSummary } from "../toast/types.d";
import natureBg1 from "../../common/assets/nature-bg-1.png";
import natureBg2 from "../../common/assets/nature-bg-2.png";
import natureBg3 from "../../common/assets/nature-bg-3.png";
import natureBg4 from "../../common/assets/nature-bg-4.png";
import natureBg5 from "../../common/assets/nature-bg-5.png";
import natureBgAgentforce from "../../common/assets/nature-bg-agentforce.png";
import { ResizeAdapter } from "common/resizeAdapter";

type ModalSize = "x-small" | "small" | "medium" | "large";

const imageBackgrounds: string[] = [
  getAssetsUrl(natureBg1),
  getAssetsUrl(natureBg2),
  getAssetsUrl(natureBg3),
  getAssetsUrl(natureBg4),
  getAssetsUrl(natureBg5),
];
const bgGradient: string =
  "linear-gradient(180deg, rgba(255, 255, 255, 0.95) 69.99%, rgba(230, 244, 254, 0.95) 99.98%);";
const agentforceGradient: string =
  "linear-gradient(180deg, rgba(255, 255, 255, 0.95) 69.99%, rgba(243, 234, 252, 0.95) 99.98%);";

export default class extends LightningElement {
  @api assistiveText: string | null = null;
  @api disableClose: BooleanAttr = null;
  @api dismissOnClickOutside: BooleanAttr = "";
  @api fullHeight: BooleanAttr = null;
  @api fullScreen: BooleanAttr = null;
  @api agentforce: BooleanAttr = null;
  @api open: BooleanAttr = null;
  @api size: ModalSize | null = null;

  private slots: string = "";
  private toastMessage: string | null = null;

  private initialOverflowValue: string = "";
  private backgroundStyle: string = "";
  private backgroundImg: string = "";

  @wire(LabelsAdapter, { locales })
  private labels: LabelsAdapterValue = null!;

  connectedCallback() {
    if (!this._disableClose) {
      window.addEventListener("keydown", this.handleKeyDown);
    }
    window.addEventListener("displaytoastmessage", this.handleShowToast);
    if (this._fullScreen) {
      this.backgroundImg = this.randomBgUrl;
      this.setResponsiveBackground();
    }
  }

  disconnectedCallback() {
    window.removeEventListener("keydown", this.handleKeyDown);
    window.removeEventListener("displaytoastmessage", this.handleShowToast);
  }

  /*
    Only called when fullScreen is set!
    We need to hide the images on screens smaller than medium max-width but also keep the gradient.
    This cannot be done via CSS as the background-image is set inline and cannot be overwritten.
  **/
  setResponsiveBackground() {
    const isSmallScreen = window.innerWidth <= 992;

    if (!isSmallScreen && this._agentforce) {
      this.backgroundStyle = `background-image: url(${getAssetsUrl(
        natureBgAgentforce
      )}), ${agentforceGradient}`;
    } else if (!isSmallScreen && !this._agentforce) {
      this.backgroundStyle = `background-image: url(${this.backgroundImg}), ${bgGradient}`;
    } else if (isSmallScreen && this._agentforce) {
      this.backgroundStyle = `background: ${agentforceGradient}`;
    } else if (isSmallScreen && !this._agentforce) {
      this.backgroundStyle = `background: ${bgGradient}`;
    }
  }

  private get isOpen() {
    return attrToBool(this.open);
  }

  private get _disableClose() {
    return attrToBool(this.disableClose);
  }

  private get _fullScreen() {
    return attrToBool(this.fullScreen);
  }

  private get _agentforce() {
    return attrToBool(this.agentforce);
  }

  private get fullScreenNotDisableClose() {
    return !attrToBool(this.disableClose) && this._fullScreen;
  }

  private get hasNoHeading() {
    return !this.slots.includes("heading");
  }

  private get dialogLabelledBy() {
    return this.assistiveText ? null : "modal-heading";
  }

  private get className() {
    return classNames("modal", {
      [`modal--${this.size}`]: this.size,
      [`modal--full-height`]: attrToBool(this.fullHeight),
      [`modal--full-screen`]: this._fullScreen,
      [`modal--agentforce`]: this._agentforce,
    });
  }

  private get dialogAriaLabel() {
    return this.assistiveText || null;
  }

  private get contentStyles() {
    return this.backgroundStyle;
  }

  private get randomBgUrl() {
    const index = Math.floor(Math.random() * imageBackgrounds.length);
    return imageBackgrounds[index];
  }

  private handleSlotChange() {
    if (!this.isOpen) {
      return;
    }
    this.slots = assignedSlotNames(this.template).join(" ");

    if (this.hasNoHeading && !this.assistiveText) {
      logger.warn("A modal must include either a heading or assistive-text.");
    }
  }

  private handleRequestClose = () => {
    this.dispatchEvent(new CustomEvent("requestclose"));
  };

  private clearShowToast = () => {
    this.toastMessage = null;
  };

  private handleShowToast = (e: Event) => {
    const toastEvent = e as CustomEvent<ToastSummary>;
    this.toastMessage = toastEvent.detail.message;

    window.setTimeout(this.clearShowToast, 2000);
  };

  // Close when user presses escape key
  private handleKeyDown = (e: KeyboardEventInit) => {
    if (e.key === "Esc" || e.key === "Escape") {
      this.handleRequestClose();
    }
  };

  // Close when user clicks outside (on backdrop)
  private handleOutsideClick(e: MouseEvent) {
    if (!attrToBool(this.dismissOnClickOutside) || this._disableClose) {
      return;
    }

    const container = this.template.querySelector(".modal__container");

    if ((e.target as HTMLElement)?.contains(container)) {
      // Prevent closing if the user is highlighing text
      requestAnimationFrame(() => {
        if (!window.getSelection()?.toString()) {
          this.handleRequestClose();
        }
      });
    }
  }

  @wire(ResizeAdapter)
  private onResize() {
    this.setResponsiveBackground();
  }
}
